import Edit from '@mui/icons-material/Edit';
import {
  Grid,
  InputLabel,
  Paper,
  Skeleton,
  TextField,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { History } from 'history';
import { Duration } from 'luxon';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';

import { trackEditAccountSettings } from 'src/common/userTracking';

import { HashRoute } from '../../common/HashRoute';
import {
  capitalize,
  formatOnOffString,
} from '../../common/helperFunctions/formatter';
import { useStylesBase } from '../../common/styles';
import { Business } from '../../lib/kixie-entities/tables/Business';
import { BusinessHarassmentPolicy } from '../../lib/kixie-entities/tables/BusinessHarassmentPolicy';
import {
  IBusinessModel,
  IUserAtom,
  IUserModel,
  user,
} from '../../recoil/atoms/user';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { createHoursString } from '../components/CadenceSettings/Scheduler';
import {
  getDefaultActionPayload2,
  IActionType,
} from '../components/dropdowns/DefaultAction';
import {
  calculateTimeDifference,
  CALL_TRANSCRIPTION_SETTINGS_STAGE,
  formatDate,
  getCallTranscriptionSettingsEndpoint,
  ICallTranscriptionSettings,
  ManageAccountModal,
} from '../components/modals/ManageAccountModal';
import {
  getCRMIcon,
  getCRMName,
} from '../pages/onboardingWalkthrough/AccountSettings';

import { DashboardTourExtraSpotlights } from './dashboardTour/DashboardTourExtraSpotlights';
const isCrmPropertiesFeatureEnabled =
  process.env.VITE_APP_CRM_PROPERTIES === '1';
const CRMNameMap: Record<string | number, string> = {
  16: 'Hubspot',
  17: 'Pipedrive',
  0: 'none',
  4: 'Salesforce',
  13: 'Zoho',
};

const useStyles = makeStyles((theme: Theme) => ({
  companyName: {
    fontWeight: 'bold',
  },
  phraseKeywordList: {
    listStyleType: 'none',
    padding: 0,
  },
  tab: {
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
    '&[name="Super Admin Settings"]': {
      border: '3px solid #ffec61',
    },
  },
  tabs: {},
  tabIcon: {
    marginRight: 15,
  },
  header: {
    color: 'var(--primary)', // pull color from primary
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    marginRight: theme.spacing(1),
    maxWidth: '87%',
  },
  modal: {
    paddingBottom: 0,
    maxHeight: 'calc(100vh - 210px)',
    overflowY: 'auto',
  },
  defaultAction: {
    marginTop: theme.spacing(2),
  },
  warningIcon: {
    color: 'orange',
  },
  searchBar: {
    padding: '0px',
    '& #searchBox': {
      width: '330px',
    },
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  spinner: {
    position: 'fixed !important' as any,
  },
  selectMenuPaper: {
    maxHeight: 460, //'calc(100% - 96px)'
  },
  outbound2: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    flexGrow: 1,
    width: '100%',
  },
  borderOutline: {
    border: `1px solid ${
      theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey'
    }`,
    width: '52px',
  },
  borderOutline2: {
    borderTop: 'none',
    backgroundColor: theme?.palette?.mode === 'dark' ? 'dimgray' : 'ghostwhite',
    minHeight: '56px',
    border: `1px solid ${
      theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey'
    }`,
  },
  billing: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    flexGrow: 1,
    width: '100%',
  },
  dealButton: {
    backgroundColor: 'rgb(104,71,255)',
    fontWeight: 700,
    '&:hover': {
      backgroundColor: 'rgb(124,91,255)',
    },
    '&:focus': {
      backgroundColor: 'rgb(134,101,255)',
    },
    '&:disabled': {
      backgroundColor: '#e0e0e0',
    },
  },
  editButton: {
    maxWidth: '155px',
    maxHeight: '50px',
    minWidth: '155px',
    minHeight: '50px',
  },
  fieldRow: {
    display: 'flex',
    marginLeft: '2px',
    marginRight: '2px',
  },
  alignCenter: {
    alignItems: 'center',
  },
  fullWidthSelect: {
    width: '100%',
    '& .MuiFormControl-marginDense': {
      width: '100%',
    },
  },
  propertiesContainer: {
    border: `1px solid ${
      theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey'
    }`,
    width: '90%',
    height: '250px',
    overflow: 'scroll',
    borderRadius: '4px',
    margin: '0px 36px 24px',
    padding: '12px',
  },
  crmPropertyCheckbox: {
    '&.Mui-checked': {
      color: 'rgb(104,71,255) !important',
    },
  },
  crmPropertyCheckboxDisabled: {
    '&.Mui-checked': {
      color: 'rgb(154,171,155) !important',
    },
  },
  checkbox: {
    userSelect: 'none',
  },
  listContainer: {
    '& > div > div': {
      paddingBottom: 0,
      paddingRight: theme.spacing(0.5),
    },
  },
  list: {
    listStyleType: 'none',
    padding: 0,
    marginBottom: 0,
  },
  listItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  listItemTextField: {
    flex: 1,
  },
  controls: {
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: theme.spacing(0.5),
  },
  editBtn: {
    padding: theme.spacing(0.5),
    minHeight: 0,
    minWidth: 0,
  },
  deleteBtn: {
    color: 'red',
    padding: theme.spacing(0.5),
    minHeight: 0,
    minWidth: 0,
  },
  addBtn: {
    marginBottom: 0,
    userSelect: 'none',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  mutedHeader: {
    fontSize: '10px',
    color: '#666666',
    display: 'block',
    margin: 0,
  },
  fourCol: {
    flex: '1 1 33.333%',
    display: 'inline-flex',
    flexDirection: 'column',
    flexGrow: 0,
    padding: '5px 0',
  },
}));

/* eslint-disable no-unused-vars */
enum EditModalHashes {
  Settings = 'settings',
  CallOptions = 'call-options',
  ManagerOptions = 'manager-options',
  Integrations = 'integrations',
  ConnectedCRMs = 'connected-crms',
  BusinessHours = 'business-hours',
  HarassmentRules = 'harassment-rules',
  CRMProperties = 'crm-properties',
  CustomCRM = 'custom-crm',
  LocalPresenceRouting = 'local-presence-routing',
  CallTranscriptions = 'call-transcriptions',
  SuperAdminSettings = 'super-admin-settings',
}
/* eslint-enable no-unused-vars */

const hash = (value) => {
  let hash = 0;
  if (value?.length === 0) return hash;
  for (let i = 0; i < value.length; i++) {
    const chr = value.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

const NOT_SET_LABEL = 'Not set';

export const MINIMUM_DURATION_MINS = 2;

export const DEFAULT_MACHINE_DETECTION_TIME_MS = 2000;

export const POWERDIALER_MACHINE_DETECT_PRODUCT_ID = 44;

const ManageAccountModalWrapper = ({
  history,
  tabIndex,
  setUsagePlan,
}: {
  history: History;
  tabIndex: number;
  setUsagePlan: (usagePlan: string) => void;
}) => (
  <ManageAccountModal
    show={true}
    setUsagePlan={setUsagePlan}
    onHide={() => {
      history.push('/manage/account');
    }}
    tabIndex={tabIndex}
  />
);

export default function ManageAccount({ history, match }: RouteComponentProps) {
  if (!history || !match) return null;

  // BEGIN DATA FETCHING CODE COPIED FROM ../components/modals/ManageAccountModal
  const [userAtomLoadable, _setUserAtom] = useRecoilStateLoadable(user);
  const userAtom = userAtomLoadable.contents as IUserAtom;

  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const {
    isBusinessEligibleToAccessCallTranscriptions,
    isMachineDetectionFeatureEnabled,
  } = permissionsLoadable.contents as IPermissions;

  const isCallTranscriptionsFeaturedEnabled =
    process.env.VITE_APP_FEATURE_CALL_TRANSCRIPTIONS === '1' &&
    isBusinessEligibleToAccessCallTranscriptions === true;

  const [businessDataCached, businessDataLoadable] = makeApiRequestCached<
    IBusinessModel | undefined
  >(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );

  const [
    _usagePlansCached,
    usagePlansLoadable,
    _setRefetchToggleForUsagePlans,
  ] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_USAGE_PLANS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_USAGE_PLANS,
      }),
    },
    (loadable, cached) => loadable.contents?.response?.data ?? cached.current
  );
  const usagePlans = useMemo(
    () =>
      usagePlansLoadable?.state === 'hasValue'
        ? [...(usagePlansLoadable?.contents?.response?.data ?? [])]?.sort(
            (a, b) => a.quotaLimit - b.quotaLimit
          )
        : [],
    [usagePlansLoadable]
  );

  const [
    businessUsersActiveCached,
    businessUsersActiveLoadable,
  ] = makeApiRequestCached<IUserModel[]>(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, cached) =>
      (loadable.contents?.response?.data?.result as IUserModel[])
        ?.filter((user) => user.isactive === 1)
        .sort((a, b) =>
          a?.fname?.toLowerCase()?.localeCompare(b?.fname?.toLowerCase())
        ) ?? cached.current
  );

  const defaultCrmUser = useMemo(
    () =>
      businessDataCached.current?.defaultCrmUserid
        ? businessUsersActiveCached.current?.find(
            (user) =>
              Number(user.userid) ===
              Number(businessDataCached.current?.defaultCrmUserid)
          )
        : {
            fname: 'None',
            lname: '',
            userid: '',
          },
    [businessDataLoadable, businessUsersActiveLoadable]
  );

  const [businessCached, businessLoadable] = makeApiRequestCached<Business>(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userAtom.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.[0] ?? cached.current
  );

  const getCurrentUsagePlan = (apiKeyObj: any) => {
    const currentPlan = usagePlans?.find(
      (plan) =>
        plan?.product === apiKeyObj?.[`${process.env.VITE_APP_REGION}`]?.product
    );
    return currentPlan?.product ?? '';
  };

  const emptyHoursObject = {
    1: {
      day: 'Monday',
      hours: [],
    },
    2: {
      day: 'Tuesday',
      hours: [],
    },
    3: {
      day: 'Wednesday',
      hours: [],
    },
    4: {
      day: 'Thursday',
      hours: [],
    },
    5: {
      day: 'Friday',
      hours: [],
    },
    6: {
      day: 'Saturday',
      hours: [],
    },
    7: {
      day: 'Sunday',
      hours: [],
    },
  };

  const crmData = {
    businessid: userAtom?.businessid,
    call: methodNameEnum.GET_CUSTOM_CRM,
    apikey: userAtom?.apikey,
  };

  const customCrmLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_CUSTOM_CRM,
      method: 'POST',
      payloadJsonString: JSON.stringify(crmData),
    })
  );

  const crmDataMemo = useMemo(
    () => customCrmLoadable?.contents?.response?.data?.result?.[0] ?? {},
    [customCrmLoadable]
  );

  const accountInitialValues = useMemo(
    () => ({
      id: userAtom.businessid ?? '',
      api: userAtom.apikey ?? '',
      accountSettings: {
        CallRecord: businessDataCached.current?.CallRecord === 1,
        sendRecordingToCRM:
          businessDataCached.current?.sendRecordingToCRM === 1,
        allowPauseRecording:
          businessDataCached.current?.allowPauseRecording === 1,
        createContact: businessDataCached.current?.createContact === 1,
        hasWebhook: businessDataCached.current?.hasWebhook === 1,
        onlyManagersCanViewCallReporting:
          businessDataCached.current?.onlyManagersCanViewCallReporting === 1,
        allowAgentPowerlist:
          businessDataCached.current?.allowAgentPowerlist === 1,
        onlyManagersCanAddPhoneNumbers:
          businessDataCached.current?.onlyManagersCanAddPhoneNumbers === 1,
        blockOutboundCallRecordingSetting:
          businessDataCached.current?.blockOutboundCallRecordingSetting ?? '',
        connectionDuration:
          businessDataCached.current?.connectionDuration ?? '',
        canDeleteUsers: businessDataCached.current?.canDeleteUsers === 1,
        showOutcomeForAllAgents:
          businessDataCached.current?.showOutcomeForAllAgents === 1,
        requireOutcomeForAllAgents:
          businessDataCached.current?.requireOutcomeForAllAgents === 1,
        autoAnswerApi: businessDataCached.current?.autoAnswerApi === 1,
        apiKeyEnabled: businessDataCached.current?.apikeyenabled === 1,
        businessDiscount: businessDataCached.current?.discountPercent ?? '',
        businessBillingTerms:
          businessDataCached.current?.businessBillingTerm ?? '',
        businessContractTerms:
          businessDataCached.current?.businessContractTerm ?? '',
        dealClosedBy: businessDataCached.current?.dealClosedBy,
        dealClosedDateTime: businessDataCached.current?.dealClosedDateTime,
        discountEndDate: businessDataCached.current?.discountEndDate,
        businessName: businessDataCached.current?.name ?? '',
        timezone:
          businessDataCached.current?.timezone_php ?? 'America/Los_Angeles',
        DncBlocksOutboundSMS:
          businessDataCached.current?.DncBlocksOutboundSMS === 1,
        stopBlocksOutboundCalls:
          businessDataCached.current?.stopBlocksOutboundCalls === 1,
        usagePlanProduct: getCurrentUsagePlan(
          businessCached?.current?.businessDetails?.apiKeyJson
        ),
        machineDetectionTime:
          businessDataCached.current?.machineDetectionTime ??
          DEFAULT_MACHINE_DETECTION_TIME_MS,
      },
      localPresencePremiumActionPayload: getDefaultActionPayload2({
        defaultAction:
          businessCached.current?.businessDetails?.localPresencePremiumAction,
      }),
      defaultCrmUserid: businessDataCached.current?.defaultCrmUserid ?? '',
      submitHoldMusicMp3File: {
        files: '',
        size: 0,
        name: businessDataCached?.current?.holdMusic ?? '',
      },
      recordNow: 'mp3',
      businessHours:
        businessCached.current?.businessDetails?.businessHours ??
        emptyHoursObject,
      customCRM: {
        crmName: crmDataMemo?.crmName ?? '',
        callsWebhook: crmDataMemo?.callsWebhook ?? '',
        hasCustomCRM: Boolean(crmDataMemo?.hasCustomCRM == '1'),
        smsWebhook: crmDataMemo?.smsWebhook ?? '',
        searchEndpoint: crmDataMemo?.searchEndpoint ?? '',
        contactUpdateEndpoint: crmDataMemo?.contactUpdateEndpoint ?? '',
        dispositionEndpoint: crmDataMemo?.dispositionEndpoint ?? '',
        activityEndpoint: crmDataMemo?.activityEndpoint ?? '',
        applyCustomCRMtoNewUsers: crmDataMemo?.applyCustomCRMtoNewUsers === 1,
        headers: crmDataMemo?.crmHeaders
          ? crmDataMemo.crmHeaders
          : [
              {
                name: '',
                value: '',
              },
            ],
      },
      businessHarassmentPolicies: businessCached.current
        ?.businessHarassmentPolicies?.length
        ? businessCached.current?.businessHarassmentPolicies?.map((policy) => {
            const luxonDuration = Duration.fromMillis(
              policy.durationMin * 60 * 1000 // 1 day
            )
              .shiftTo('days', 'hours', 'minutes', 'seconds', 'milliseconds')
              .toObject();
            return {
              ...policy,
              days: luxonDuration.days,
              hours: luxonDuration.hours,
              minutes: luxonDuration.minutes,
            } as BusinessHarassmentPolicy & {
              days: number;
              hours: number;
              minutes: number;
            };
          })
        : ([
            {
              channel: 'call',
              frequency: 3,
              durationMin: 60 * 24,
              days: 1,
              hours: 0,
              minutes: 0,
            },
            {
              channel: 'sms',
              frequency: 2,
              durationMin: 60 * 24,
              days: 1,
              hours: 0,
              minutes: 0,
            },
          ] as (BusinessHarassmentPolicy & {
            days: number;
            hours: number;
            minutes: number;
          })[]),
    }),
    [
      userAtomLoadable,
      businessDataLoadable,
      businessLoadable,
      crmDataMemo,
      usagePlans,
    ]
  );
  // END DATA FETCHING CODE COPIED FROM ../components/modals/ManageAccountModal

  const [minutesSaved, setMinutesSaved] = useState(0);
  const [minutesSavedState, setMinutesSavedState] = useState('loading');
  const event = {
    call: 'getBusinessUsersTimeSavedReporting',
    // connectionLimit: '90',
    // enddate: '2020-07-29',
    // startdate: '2020-07-16',
    timezone: 'America/Los_Angeles',
    connectionLimit: businessCached?.current?.connectionDuration,
    businessid: userAtom.businessid,
    apiKey: userAtom.apikey,
    startdate: '1970-01-01',
    enddate: new Date().toISOString().split('T')[0],
  };

  const fetchTimeSavedReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USER_TIME_SAVED_REPORTING,
      payloadJsonString: JSON.stringify(event),
    })
  );

  useEffect(() => {
    if (!userAtom.businessid) return;
    if (fetchTimeSavedReportLoadable.state === 'hasValue') {
      setMinutesSaved(
        fetchTimeSavedReportLoadable?.contents.response?.data?.business?.totals
          ?.total || 0
      );
      setMinutesSavedState('hasValue');
    }
  }, [fetchTimeSavedReportLoadable, userAtom]);

  const getBusinessStatsEndpoint = () =>
    userAtom.businessid
      ? endpointEnum.GET_BUSINESS_STATS.replace(
          '{businessId}',
          userAtom.businessid.toString()
        )
      : '';

  const [
    _,
    businessStatsLoadable,
    _setRefetchToggleForBusinessStats,
  ] = makeApiRequestCached(
    {
      method: 'get',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: getBusinessStatsEndpoint(),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.data ?? cached.current
  );

  const businessStats = {
    agents: businessStatsLoadable.contents?.response?.data?.data?.agents || 0,
    callsMade:
      businessStatsLoadable.contents?.response?.data?.data?.callsMade || 0,
    minutesSaved,
  };
  const isBusinessStatsLoading =
    businessStatsLoadable.state === 'loading' ||
    minutesSavedState === 'loading';

  const [
    ,
    getBusinessCRMPropertiesLoadable,
    _setRefetchToggleForGetBusinessCRMProperties,
  ] = makeApiRequestCached(
    {
      method: 'post',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      payloadJsonString: JSON.stringify({
        search: 'active',
      }),
      endpoint: endpointEnum.BUSINESS_CRM + `/${userAtom.businessid}`,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.data ?? cached.current
  );

  const loginUser = userAtom?.user ?? {};
  const isSuperAdmin = loginUser.role === 'superadmin';

  const callRules = accountInitialValues.businessHarassmentPolicies.find(
    (policy) => policy.channel === 'call'
  ) || {
    frequency: 0,
    days: 0,
    hours: 0,
    minutes: 0,
  };

  const smsRules = accountInitialValues.businessHarassmentPolicies.find(
    (policy) => policy.channel === 'sms'
  ) || {
    frequency: 0,
    days: 0,
    hours: 0,
    minutes: 0,
  };

  const formatRulesTimeString = (rules) => {
    if (rules.days === 1 && !rules.hours && !rules.minutes) {
      return 'day';
    } else if (!rules.days && rules.hours === 1 && !rules.minutes) {
      return 'hour';
    } else if (!rules.days && !rules.hours && rules.minutes === 1) {
      return 'minute';
    }

    let result = '';
    if (rules.days >= 1) {
      if (result.length > 0) {
        result += ' and';
      }
      result += ` ${rules.days} ${rules.days === 1 ? 'day' : 'days'}`;
    }
    if (rules.hours >= 1) {
      if (result.length > 0) {
        result += ' and';
      }
      result += ` ${rules.hours} ${rules.hours === 1 ? 'hour' : 'hours'}`;
    }
    if (rules.minutes >= 1) {
      if (result.length > 0) {
        result += ' and';
      }
      result += ` ${rules.minutes} ${
        rules.minutes === 1 ? 'minute' : 'minutes'
      }`;
    }
    return result.trimLeft();
  };

  const getLocalPresenceActionString = (type: IActionType) => {
    switch (type) {
      case 'ivr':
        return type.toUpperCase();
      default:
        return capitalize(type);
    }
  };

  const getOutboundRecordingString = (setting: string) => {
    switch (setting) {
      case 'all':
        return 'None';
      case 'none':
        return 'All';
      case 'twoparty':
        return 'Two Party';
      default:
        return capitalize(setting);
    }
  };

  // used because return value from updateBusines2
  // does not reflect second lambda invocation
  const [usagePlanProduct, setUsagePlanProduct] = React.useState('');
  if (
    usagePlanProduct !==
      accountInitialValues.accountSettings.usagePlanProduct &&
    usagePlanProduct === ''
  ) {
    setUsagePlanProduct(accountInitialValues.accountSettings.usagePlanProduct);
  }

  const plan = usagePlans.find(
    (usagePlan) => usagePlan.product === usagePlanProduct
  );

  const useCustomCRM = accountInitialValues.customCRM.hasCustomCRM;
  const account = {
    stats: {
      agents: businessStats.agents.toLocaleString(),
      callsMade: businessStats.callsMade.toLocaleString(),
      minutesSaved: businessStats.minutesSaved.toLocaleString(),
    },
    settings: {
      businessName: accountInitialValues.accountSettings.businessName,
      businessTimezone: accountInitialValues.accountSettings.timezone,
      businessHours: createHoursString(accountInitialValues.businessHours),
      holdMusic: accountInitialValues.submitHoldMusicMp3File.name,
    },
    options: {
      connectionDuration: accountInitialValues.accountSettings.connectionDuration.toString(),
      outboundRecording: getOutboundRecordingString(
        accountInitialValues.accountSettings.blockOutboundCallRecordingSetting
      ),
      recordCalls: formatOnOffString(
        accountInitialValues.accountSettings.CallRecord
      ),
      managersOnlyViewCallReporting: formatOnOffString(
        accountInitialValues.accountSettings.onlyManagersCanViewCallReporting
      ),
      sendRecordingsToCRM: formatOnOffString(
        accountInitialValues.accountSettings.sendRecordingToCRM
      ),
      allowPauseRecording: formatOnOffString(
        accountInitialValues.accountSettings.allowPauseRecording
      ),
      allowAgentPowerlists: formatOnOffString(
        accountInitialValues.accountSettings.allowAgentPowerlist
      ),
      createContacts: formatOnOffString(
        accountInitialValues.accountSettings.createContact
      ),
      managersOnlyAddPhoneNumbers: formatOnOffString(
        accountInitialValues.accountSettings.onlyManagersCanAddPhoneNumbers
      ),
      allowWebhooks: formatOnOffString(
        accountInitialValues.accountSettings.hasWebhook
      ),
      showOutcomeForAllAgents: formatOnOffString(
        accountInitialValues.accountSettings.showOutcomeForAllAgents
      ),
      requireOutcomeForAllAgents: formatOnOffString(
        accountInitialValues.accountSettings.requireOutcomeForAllAgents
      ),
      machineDetectionTime:
        Math.round(
          (accountInitialValues.accountSettings.machineDetectionTime / 1000) *
            100
        ) / 100,
    },
    crmProperties: {
      crm:
        CRMNameMap[
          getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]
            ?.crmID ?? 0
        ] ?? 'none',
      lastUpdatedDate: getBusinessCRMPropertiesLoadable?.contents?.response
        ?.data?.[0]?.updatedDateTime
        ? moment(
            getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]
              ?.updatedDateTime
          ).format('MM/DD/YYYY @ h:mm a')
        : 'N/A',
    },
    integrations: {
      businessId: accountInitialValues.id,
      apiKey: accountInitialValues.api,
    },
    localPresenceRouting: {
      localPresenceAction: getLocalPresenceActionString(
        accountInitialValues.localPresencePremiumActionPayload.type
      ),
    },
    harassmentRules: {
      callRules: `${callRules.frequency || 0} calls per ${formatRulesTimeString(
        callRules
      )}`,
      smsRules: `${smsRules.frequency || 0} texts per ${formatRulesTimeString(
        smsRules
      )}`,
    },
    customCRM: {
      crmName: accountInitialValues.customCRM.crmName,
      useCustomCRM: formatOnOffString(useCustomCRM),
      applyToAllUsers: formatOnOffString(
        accountInitialValues.customCRM.applyCustomCRMtoNewUsers
      ),
      callWebhook: accountInitialValues.customCRM.callsWebhook,
      smsWebhook: accountInitialValues.customCRM.smsWebhook,
      searchEndpoint: accountInitialValues.customCRM.searchEndpoint,
      contactEndpoint: accountInitialValues.customCRM.contactUpdateEndpoint,
      dispositionsEndpoint: accountInitialValues.customCRM.dispositionEndpoint,
      activitiesEndpoint: accountInitialValues.customCRM.activityEndpoint,
      headers: accountInitialValues.customCRM.headers,
    },
    superAdminSettings: {
      defaultCRMUser: defaultCrmUser
        ? `${defaultCrmUser.fname} ${defaultCrmUser.lname}`
        : 'None',
      discountPercent: accountInitialValues.accountSettings.businessDiscount,

      billingTerms: accountInitialValues.accountSettings.businessBillingTerms,
      contractTerms: accountInitialValues.accountSettings.businessContractTerms,
      dealClosedBy: accountInitialValues.accountSettings.dealClosedBy,
      dealClosedDateTime:
        accountInitialValues.accountSettings.dealClosedDateTime,
      discountEndDate: accountInitialValues.accountSettings.discountEndDate,
      apiKeyEnabled: formatOnOffString(
        accountInitialValues.accountSettings.apiKeyEnabled
      ),
      apiLevel: plan
        ? `${plan.quotaLimit.toLocaleString({}, { locale: 'en-US' })} per ${
            plan.quotaPeriod
          }`
        : '',
      canDeleteUsers: formatOnOffString(
        accountInitialValues.accountSettings.canDeleteUsers
      ),
      autoAnswerCallEvent: formatOnOffString(
        accountInitialValues.accountSettings.autoAnswerApi
      ),
      blockOutboundSMS: formatOnOffString(
        accountInitialValues.accountSettings.DncBlocksOutboundSMS
      ),
      blockOutboundCalls: formatOnOffString(
        accountInitialValues.accountSettings.stopBlocksOutboundCalls
      ),
    },
  };

  const [
    callTranscriptionSettingsCached,
    callTranscriptionSettingsLoadable,
  ] = makeApiRequestCached<ICallTranscriptionSettings>(
    {
      endpoint: getCallTranscriptionSettingsEndpoint(userAtom),
      method: 'get',
      stage: CALL_TRANSCRIPTION_SETTINGS_STAGE,
      domain: process.env.VITE_APP_CDK_DOMAIN,
    },
    (loadable, cached) => loadable.contents?.response?.data ?? cached.current
  );

  const crmStats = {
    businessid: userAtom?.businessid,
    call: methodNameEnum.GET_BUSINESS_CRM_STATS,
    apikey: userAtom?.apikey,
  };
  const crmStatsLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_CRM_STATS,
      method: 'POST',
      payloadJsonString: JSON.stringify(crmStats),
    })
  );
  const crmStatsMemo = useMemo(
    () => crmStatsLoadable?.contents?.response?.data?.result ?? {},
    [crmStatsLoadable]
  );

  const isCallTranscriptionSettingsLoading =
    callTranscriptionSettingsLoadable.state === 'loading';

  const classes = useStylesBase();
  const classesCustom = useStyles();

  const renderEditModal = (tabIndex) => () => {
    const setUsagePlan = (usagePlan) => setUsagePlanProduct(usagePlan);

    return (
      <ManageAccountModalWrapper
        history={history}
        tabIndex={tabIndex}
        setUsagePlan={setUsagePlan}
      />
    );
  };

  const getEditUrl = (hash) => `${match.url}#${hash}`;

  const dealClosedDate =
    businessDataCached.current?.dealClosedDateTime?.slice?.(0, 10) ?? '';
  const dealClosedBy = useMemo(() => {
    if (businessUsersActiveCached.current && businessDataCached.current) {
      return (
        (businessUsersActiveCached.current ?? [])?.find(
          (user) => user.userid === businessDataCached.current?.dealClosedBy
        )?.calleridName ?? businessDataCached.current?.dealClosedBy
      );
    }
  }, [
    businessUsersActiveCached.current,
    businessDataCached.current?.dealClosedBy,
  ]);

  const businessUsersLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    })
  );

  const hasAnyAgentWithPowerDialerMachineDetectionProduct: boolean = useMemo(() => {
    return (businessUsersLoadable.contents?.response?.data?.result ?? []).some(
      (user: any) =>
        user?.powerlistProductId == POWERDIALER_MACHINE_DETECT_PRODUCT_ID
    );
  }, [businessUsersLoadable]);

  const isMachineDetectionSectionVisible =
    isMachineDetectionFeatureEnabled &&
    hasAnyAgentWithPowerDialerMachineDetectionProduct;

  return (
    <>
      <DashboardTourExtraSpotlights />
      {Object.values(EditModalHashes).map((hash, index) => {
        if (
          (hash === EditModalHashes.CRMProperties &&
            !isCrmPropertiesFeatureEnabled) ||
          (hash === EditModalHashes.CallTranscriptions &&
            !isCallTranscriptionsFeaturedEnabled)
        ) {
          return null;
        }

        let tabIndex = index;

        if (
          !isCrmPropertiesFeatureEnabled &&
          [
            EditModalHashes.CustomCRM,
            EditModalHashes.LocalPresenceRouting,
            EditModalHashes.CallTranscriptions,
            EditModalHashes.SuperAdminSettings,
          ].includes(hash)
        ) {
          tabIndex -= 1;
        }

        if (
          !isCallTranscriptionsFeaturedEnabled &&
          [EditModalHashes.SuperAdminSettings].includes(hash)
        ) {
          tabIndex -= 1;
        }

        return (
          <HashRoute
            path={match.url}
            hash={`#${hash}`}
            render={renderEditModal(tabIndex)}
            key={hash}
          />
        );
      })}
      <Paper className={classes.paper} elevation={0}>
        <Grid
          container
          spacing={2}
          className={clsx(classes.topContainer, 'dtStepBusinessMetrics')}
        >
          <Grid item xs={10}>
            <h1 data-qa="accont-settings-business-name">
              Account Settings for{' '}
              <span
                data-qa="account-settings-business-name"
                className={classesCustom.companyName}
              >
                {account.settings.businessName}
              </span>
            </h1>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.Settings)}
              onClick={() => {
                trackEditAccountSettings();
              }}
              className={classes.editAllLink}
            >
              <Edit />
              <br />
              <span data-qa="edit-all-pencil">Edit All</span>
            </Link>
          </Grid>
          <Grid data-qa="agent-count" item xs={12} sm={4} md={4}>
            <span className={classes.statsLabel}>Agents</span>
            {isBusinessStatsLoading ? (
              <Skeleton
                variant="rectangular"
                className={classes.statsValueLoading}
              />
            ) : (
              <p data-qa="total-agent-count" className={classes.statsValue}>
                {account.stats.agents || ' '}
              </p>
            )}
          </Grid>
          <Grid data-qa="calls-made" item xs={12} sm={4} md={4}>
            <span className={classes.statsLabel}>Calls Made</span>
            {isBusinessStatsLoading ? (
              <Skeleton
                variant="rectangular"
                className={classes.statsValueLoading}
              />
            ) : (
              <p data-qa="total-calls-made" className={classes.statsValue}>
                {account.stats.callsMade || ' '}
              </p>
            )}
          </Grid>
          <Grid data-qa="minutes-saved" item xs={12} sm={4} md={4}>
            <span className={classes.statsLabel}>Minutes Saved</span>
            {isBusinessStatsLoading ? (
              <Skeleton
                variant="rectangular"
                className={classes.statsValueLoading}
              />
            ) : (
              <p data-qa="total-minutes-saved" className={classes.statsValue}>
                {account.stats.minutesSaved || ' '}
              </p>
            )}
          </Grid>
        </Grid>
        <hr className={classes.topDivider} />
        <Grid container spacing={2} className={classes.sectionContainer}>
          <Grid item xs={10}>
            <h2
              data-qa="settings-subheader-title"
              className={classes.sectionTitle}
            >
              Manage
            </h2>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.Settings)}
              className={classes.editLink}
            >
              <Edit data-qa="settings-edit-pencil" />
            </Link>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              data-qa="business-name"
              label="Business Name"
              value={account.settings.businessName}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Business Timezone *"
              data-qa="business-timezone"
              value={account.settings.businessTimezone}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Business Hours"
              name="data-qa-business-hours-set"
              value={account.settings.businessHours}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <Grid data-qa="hold-music" item xs={12} sm={6} md={4}>
            <InputLabel variant="standard" shrink>
              Hold Music
            </InputLabel>
            {account.settings.holdMusic && (
              <audio
                data-qa="hold-music-audio"
                controls
                className={classes.audioPlayer}
              >
                <source src={account.settings.holdMusic} type="audio/mpeg" />
              </audio>
            )}
          </Grid>
        </Grid>
      </Paper>
      <Paper className={classes.paper} elevation={0}>
        <Grid container spacing={2} className={classes.sectionContainer}>
          <Grid item xs={10}>
            <h2
              data-qa="options-subheader-title"
              className={classes.sectionTitle}
            >
              Options
            </h2>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.CallOptions)}
              className={classes.editLink}
            >
              <Edit data-qa="options-edit-pencil" />
            </Link>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Connection Duration"
              data-qa="connection-duration"
              value={account.options.connectionDuration}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Outbound Recording"
              data-qa="outbound-recording"
              value={account.options.outboundRecording}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Record Calls"
              data-qa="record-calls"
              value={account.options.recordCalls}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Managers Only View Call Reporting"
              data-qa="managers-only-view-call-reporting"
              value={account.options.managersOnlyViewCallReporting}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Send Recordings to CRM"
              data-qa="send-recording-to-crm"
              value={account.options.sendRecordingsToCRM}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Allow Pause Recording"
              data-qa="allow-pause-recording"
              value={account.options.allowPauseRecording}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Allow Agent Powerlists"
              data-qa="allow-agent-powerlist"
              value={account.options.allowAgentPowerlists}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Create Contacts"
              data-qa="create-contacts"
              value={account.options.createContacts}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Managers Only Add Phone Numbers"
              data-qa="manager-only-add-phone-numbers"
              value={account.options.managersOnlyAddPhoneNumbers}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Allow Webhooks"
              data-qa="allow-webhooks"
              value={account.options.allowWebhooks}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
          {process.env.VITE_APP_FEATURE_OUTCOME === '1' && (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Show Outcome for All Agents"
                  data-qa="show-outcome-for-all-agents"
                  value={account.options.showOutcomeForAllAgents}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Require Outcome for All Agents"
                  data-qa="require-outcome-for-all-agents"
                  value={account.options.requireOutcomeForAllAgents}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
            </>
          )}
          {isMachineDetectionSectionVisible && (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="AI Human Detect Timeout Duration"
                  data-qa="ai-human-detect-timeout-duration"
                  value={account.options.machineDetectionTime}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
      <Paper className={classes.paper} elevation={0}>
        <Grid container spacing={2} className={classes.sectionContainer}>
          <Grid item xs={10}>
            <h2
              data-qa="integration-subheader"
              className={classes.sectionTitle}
            >
              Integrations
            </h2>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.Integrations)}
              className={classes.editLink}
            >
              <Edit data-qa="integrations-edit-pencil" />
            </Link>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Business Id"
              data-qa="business-id"
              value={account.integrations.businessId}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="API Key"
              data-qa="api-key"
              value={account.integrations.apiKey}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
        </Grid>
      </Paper>
      <Paper className={classes.paper} elevation={0}>
        <Grid container spacing={2} className={classes.sectionContainer}>
          <Grid item xs={10}>
            <h2
              data-qa="connected-crms-subheader"
              className={classes.sectionTitle}
            >
              Connected CRMs
            </h2>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.ConnectedCRMs)}
              className={classes.editLink}
            >
              <Edit data-qa="connected-crms-edit-pencil" />
            </Link>
          </Grid>
          <div style={{ margin: '0px 24px' }}>
            {crmStatsMemo?.length ? (
              (crmStatsMemo ?? []).map((elem, elemKey) => {
                return (
                  <div
                    key={elemKey}
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      borderBottom: '1px solid #eeeeee',
                      padding: '15px 0',
                    }}
                  >
                    <div
                      style={{
                        margin: '-50px 0 0',
                        width: '120px',
                        maxWidth: '120px',
                        minWidth: '120px',
                      }}
                    >
                      <div>
                        <img
                          style={{
                            borderRadius: '50%',
                            height: '72px',
                            width: '72px',
                            border: '1px solid #eeeeee',
                            boxShadow:
                              'rgba(184, 184, 184, 0.34) 0px 0px 20px -2px',
                          }}
                          src={getCRMIcon(elem?.crmName)}
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        width: '70%',
                      }}
                    >
                      <div className={classesCustom.fourCol}>
                        <p className={classesCustom.mutedHeader}>
                          Connected CRM
                        </p>
                        {getCRMName(elem.crmName)}
                      </div>
                      {!!(elem.orgid || elem.location) && (
                        <div className={classesCustom.fourCol}>
                          <p className={classesCustom.mutedHeader}>
                            Account ID
                          </p>
                          {elem.orgid ? elem.orgid : elem.location}
                        </div>
                      )}
                      {!!elem.countUsers && (
                        <div className={classesCustom.fourCol}>
                          <p className={classesCustom.mutedHeader}>
                            Number of Users
                          </p>
                          {elem.countUsers}
                        </div>
                      )}
                      {!!elem.calleridName && (
                        <div className={classesCustom.fourCol}>
                          <p className={classesCustom.mutedHeader}>
                            Connected Admin
                          </p>
                          {elem.calleridName}
                        </div>
                      )}
                      {!!elem.created && (
                        <div className={classesCustom.fourCol}>
                          <p className={classesCustom.mutedHeader}>
                            Connected Date
                          </p>
                          {formatDate(elem.created)}
                        </div>
                      )}
                      {!!elem.totalSynced && (
                        <div className={classesCustom.fourCol}>
                          <p className={classesCustom.mutedHeader}>
                            Contacts Synced
                          </p>
                          {elem.totalSynced}
                        </div>
                      )}
                      {!!elem.status && (
                        <div className={classesCustom.fourCol}>
                          <p className={classesCustom.mutedHeader}>
                            Sync Status
                          </p>
                          {elem.status}
                        </div>
                      )}
                      {!!elem.startDate && (
                        <div className={classesCustom.fourCol}>
                          <p className={classesCustom.mutedHeader}>
                            Sync Duration
                          </p>
                          {calculateTimeDifference(
                            elem.startDate,
                            elem.endDate
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div></div>
            )}
          </div>
        </Grid>
      </Paper>
      <Paper className={classes.paper} elevation={0}>
        <Grid container spacing={2} className={classes.sectionContainer}>
          <Grid item xs={10}>
            <h2
              data-qa="local-presence-routing-subheader"
              className={classes.sectionTitle}
            >
              Local Presence Routing
            </h2>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.LocalPresenceRouting)}
              className={classes.editLink}
            >
              <Edit data-qa="local-presence-edit-pencil" />
            </Link>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Local Presence Action"
              data-qa="local-presence-action"
              value={account.localPresenceRouting.localPresenceAction}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>
      <Paper className={classes.paper} elevation={0}>
        <Grid container spacing={2} className={classes.sectionContainer}>
          <Grid item xs={10}>
            <h2
              data-qa="harassment-rules-subheader"
              className={classes.sectionTitle}
            >
              Harassment Rules
            </h2>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.HarassmentRules)}
              className={classes.editLink}
            >
              <Edit data-qa="harassment-rules-edit-pencil" />
            </Link>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Call Rules"
              data-qa="call-rules"
              value={account.harassmentRules.callRules}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="SMS Rules"
              data-qa="sms-rules"
              value={account.harassmentRules.smsRules}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
        </Grid>
      </Paper>
      <Paper className={classes.paper} elevation={0}>
        <Grid container spacing={2} className={classes.sectionContainer}>
          <Grid item xs={10}>
            <h2
              data-qa="crm-properties-subheader"
              className={classes.sectionTitle}
            >
              CRM Properties
            </h2>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.CRMProperties)}
              className={classes.editLink}
            >
              <Edit data-qa="crm-properties-edit-pencil" />
            </Link>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="CRM"
              data-qa="crm-name"
              value={account.crmProperties.crm}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Last Imported"
              data-qa="last-imported"
              value={account.crmProperties.lastUpdatedDate}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
        </Grid>
      </Paper>
      <Paper className={classes.paper} elevation={0}>
        <Grid container spacing={2} className={classes.sectionContainer}>
          <Grid item xs={10}>
            <h2 data-qa="custom-crm-subheader" className={classes.sectionTitle}>
              Custom CRM
            </h2>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.CustomCRM)}
              className={classes.editLink}
            >
              <Edit data-qa="customer-crm-edit-pencil" />
            </Link>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Use Custom CRM"
              data-qa="use-custom-crm"
              value={account.customCRM.useCustomCRM}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          {useCustomCRM && (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="CRM Name"
                  data-qa="custom-crm-name"
                  value={(account.customCRM.crmName || NOT_SET_LABEL).trim()}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Apply To All Users"
                  data-qa="custom-crm-apply-to-all-users"
                  value={account.customCRM.applyToAllUsers}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Call Webhook"
                  data-qa="custom-crm-call-webhook"
                  value={(
                    account.customCRM.callWebhook || NOT_SET_LABEL
                  ).trim()}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="SMS Webhook"
                  data-qa="customer-crm-sms-webhook"
                  value={(account.customCRM.smsWebhook || NOT_SET_LABEL).trim()}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Search Endpoint *"
                  data-qa="custom-crm-search-endpoint"
                  value={(
                    account.customCRM.searchEndpoint || NOT_SET_LABEL
                  ).trim()}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Contact Endpoint"
                  data-qa="contact-endpoint"
                  value={(
                    account.customCRM.contactEndpoint || NOT_SET_LABEL
                  ).trim()}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Call Outcome Endpoint"
                  data-qa="custom-crm-dispositions-endpoint"
                  value={(
                    account.customCRM.dispositionsEndpoint || NOT_SET_LABEL
                  ).trim()}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Activities Endpoint"
                  data-qa="custom-crm-activities-endpoint"
                  value={(
                    account.customCRM.activitiesEndpoint || NOT_SET_LABEL
                  ).trim()}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
              {account.customCRM.headers.map((header) => {
                if (!header.name && !header.value) return null;
                return (
                  <div key={header.name}>
                    <br />
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Header Name"
                        data-qa="custom-crm-header-name"
                        value={(header.name || NOT_SET_LABEL).trim()}
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                        }}
                        variant="standard"
                        fullWidth
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Header Value"
                        data-qa="custom-crm-header-value"
                        value={(header.value || NOT_SET_LABEL).trim()}
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                        }}
                        variant="standard"
                        fullWidth
                        multiline
                      />
                    </Grid>
                  </div>
                );
              })}
            </>
          )}
        </Grid>
      </Paper>
      {isCallTranscriptionsFeaturedEnabled && (
        <Paper className={classes.paper} elevation={0}>
          <Grid container spacing={2} className={classes.sectionContainer}>
            <Grid item xs={10}>
              <h2
                data-qa="conversation-intelligence-subheader"
                className={classes.sectionTitle}
              >
                Conversation Intelligence
              </h2>
            </Grid>
            <Grid item xs={2} className={classes.editButtonContainer}>
              <Link
                to={getEditUrl(EditModalHashes.CallTranscriptions)}
                className={classes.editLink}
              >
                <Edit data-qa="call-transcription-edit-pencil" />
              </Link>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              {isCallTranscriptionSettingsLoading ? (
                <>
                  <InputLabel shrink>
                    Min. Duration for Conversation Intelligence
                  </InputLabel>
                  <Skeleton
                    variant="rectangular"
                    className={classes.statsValueLoading}
                  />
                </>
              ) : (
                <TextField
                  data-qa="converstation-intelligence-subsection-min-duration-for-conversation-intelligence"
                  label="Min. Duration for Conversation Intelligence"
                  value={`${
                    callTranscriptionSettingsCached.current?.minimumDuration ??
                    MINIMUM_DURATION_MINS
                  } Minutes`}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {isCallTranscriptionSettingsLoading ? (
                <>
                  <InputLabel shrink>
                    Conversation Strength Calculator Keywords
                  </InputLabel>
                  <Skeleton
                    variant="rectangular"
                    className={classes.statsValueLoading}
                  />
                </>
              ) : (
                <TextField
                  label="Conversation Strength Calculator Keywords"
                  data-qa="conversation-strength-calculator-keywords"
                  value={
                    callTranscriptionSettingsCached.current
                      ?.isCallStrengthKeywordsEnabled
                      ? 'ON'
                      : 'OFF'
                  }
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {isCallTranscriptionSettingsLoading ? (
                <>
                  <InputLabel shrink>
                    Conversation Strength Calculator Phrases
                  </InputLabel>
                  <Skeleton
                    variant="rectangular"
                    className={classes.statsValueLoading}
                  />
                </>
              ) : (
                <TextField
                  label="Conversation Strength Calculator Phrases"
                  data-qa="conversation-strength-calculator-phrases"
                  value={
                    callTranscriptionSettingsCached.current
                      ?.isCallStrengthPhrasesEnabled
                      ? 'ON'
                      : 'OFF'
                  }
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {isCallTranscriptionSettingsLoading ? (
                <>
                  <InputLabel shrink>
                    Conversation Strength Calculator Call Sentiment
                  </InputLabel>
                  <Skeleton
                    variant="rectangular"
                    className={classes.statsValueLoading}
                  />
                </>
              ) : (
                <TextField
                  label="Conversation Strength Calculator Call Sentiment"
                  data-qa="conversation-strength-calculator-call-sentiment"
                  value={
                    callTranscriptionSettingsCached.current
                      ?.isCallStrengthCallSentimentEnabled
                      ? 'ON'
                      : 'OFF'
                  }
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              {isCallTranscriptionSettingsLoading ? (
                <>
                  <InputLabel shrink>Phrases</InputLabel>
                  <Skeleton
                    variant="rectangular"
                    className={classes.statsValueLoading}
                  />
                </>
              ) : (
                <>
                  <InputLabel shrink>Phrases</InputLabel>
                  <ul
                    data-qa="conversation-intelligence-subsection-phrases"
                    className={classesCustom.phraseKeywordList}
                  >
                    {(
                      callTranscriptionSettingsCached.current?.phrases ?? []
                    ).map((phrase, i) => (
                      <li key={`phrase-${i}-${hash(phrase)}`}>{phrase}</li>
                    ))}
                    {(callTranscriptionSettingsCached.current?.phrases ?? [])
                      .length === 0 ? (
                      <li>None</li>
                    ) : undefined}
                  </ul>
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              {isCallTranscriptionSettingsLoading ? (
                <>
                  <InputLabel shrink>Keywords</InputLabel>
                  <Skeleton
                    variant="rectangular"
                    className={classes.statsValueLoading}
                  />
                </>
              ) : (
                <>
                  <InputLabel shrink>Keywords</InputLabel>
                  <ul
                    data-qa="conversation-intelligence-subsection-keywords"
                    className={classesCustom.phraseKeywordList}
                  >
                    {(
                      callTranscriptionSettingsCached.current?.keywords ?? []
                    ).map((keyword, i) => (
                      <li key={`keyword-${i}-${hash(keyword)}`}>{keyword}</li>
                    ))}
                    {(callTranscriptionSettingsCached.current?.keywords ?? [])
                      .length === 0 ? (
                      <li>None</li>
                    ) : undefined}
                  </ul>
                </>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}
      {isSuperAdmin && (
        <Paper className={classes.paper + ' superStyle'} elevation={0}>
          <Grid container spacing={2} className={classes.sectionContainer}>
            <Grid item xs={10}>
              <h2
                data-qa="super-admin-title"
                className={classes.sectionTitle + ' section-title'}
              >
                Super Admin Settings
              </h2>
            </Grid>
            <Grid item xs={2} className={classes.editButtonContainer}>
              <Link
                to={getEditUrl(EditModalHashes.SuperAdminSettings)}
                className={classes.editLink}
              >
                <Edit data-qa="super-admin-edit-pencil" />
              </Link>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Default CRM User *"
                data-qa="default-crm-user"
                value={account.superAdminSettings.defaultCRMUser}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="API Key Enabled"
                data-qa="api-key-enabled"
                value={account.superAdminSettings.apiKeyEnabled}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Billing Terms"
                data-qa="billing-terms"
                value={account.superAdminSettings.billingTerms}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Can Delete Users"
                data-qa="can-delete-users"
                value={account.superAdminSettings.canDeleteUsers}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="API Level"
                data-qa="api-level"
                value={(
                  account.superAdminSettings.apiLevel || NOT_SET_LABEL
                ).trim()}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Contract Terms"
                data-qa="contract-terms"
                value={account.superAdminSettings.contractTerms}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Auto Answer Call Event"
                data-qa="auto-answer-call-event"
                value={account.superAdminSettings.autoAnswerCallEvent}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Discount %"
                data-qa="discount-percent"
                value={account.superAdminSettings.discountPercent}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Deal Closed?"
                data-qa="deal-closed"
                value={account.superAdminSettings.dealClosedBy ? 'YES' : 'NO'}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Block Outbound SMS"
                data-qa="block-outbound-sms"
                value={account.superAdminSettings.blockOutboundSMS}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Discount Length"
                data-qa="discount-length"
                value={
                  account.superAdminSettings.discountEndDate
                    ? 'Has End Date'
                    : 'Perpetual (No End Date)'
                }
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
              />
            </Grid>
            {dealClosedDate ? (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Deal Closed Details"
                  data-qa="deal-closed-details"
                  value={`Closed on ${dealClosedDate}\n${dealClosedBy}`}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={6} md={4}>
                {/* <TextField
                  label="Block Outbound Calls"
                  value={account.superAdminSettings.blockOutboundCalls}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                /> */}
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Block Outbound Calls"
                data-qa="block-outbound-calls"
                value={account.superAdminSettings.blockOutboundCalls}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
              />
            </Grid>
            {!!account.superAdminSettings.discountEndDate && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Discount % End Date"
                  data-qa="discount-end-date"
                  value={account.superAdminSettings.discountEndDate?.substring(
                    0,
                    10
                  )}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      )}
    </>
  );
}
